import { Stack, Typography, useTheme } from '@mui/material';
import { useAppTFunction } from '@retail/app/i18n';
import { CalculusArea } from '@retail/calculus/types';
import { Chip } from '@shared/components';
import { useMemo } from 'react';

interface Props {
  calculusArea: CalculusArea;
  salesPriceConditionsIsEnabled?: boolean;
  width?: number;
  height?: number;
  rounded?: boolean;
  customLabel?: string;
  labelDetails?: string;
}

export const ConditionTypeChip = ({
  calculusArea,
  salesPriceConditionsIsEnabled,
  width = 144,
  height = 36,
  rounded,
  customLabel,
  labelDetails,
}: Props) => {
  const t = useAppTFunction();
  const theme = useTheme();
  const palette = theme.vars.palette;

  const chipLabel = useMemo(() => {
    switch (calculusArea) {
      case 'PURCHASE':
        return t('app.routes.agreements');

      case 'FREIGHT':
        return t('app.routes.freight');

      case 'MARKUP':
        return t('app.routes.markup');

      case 'PRICE_SUGGESTION':
      case 'PRICE_SOURCE':
        return salesPriceConditionsIsEnabled
          ? t('app.routes.salesPriceCondition')
          : t('app.routes.priceSuggestion');

      case 'CUSTOMER_MARKUP':
        return t('app.routes.customerMarkup');

      case 'ROUNDING':
        return t('app.routes.roundingConditions');

      default:
        return '-';
    }
  }, [calculusArea, salesPriceConditionsIsEnabled, t]);

  const chipColor = useMemo(() => {
    const { tint } = palette.semantic;
    switch (calculusArea) {
      case 'PURCHASE':
        return { bg: tint.sky.soft, text: tint.sky.text };
      case 'FREIGHT':
        return { bg: tint.lavender.soft, text: tint.lavender.text };
      case 'MARKUP':
        return { bg: tint.green.soft, text: tint.green.text };
      case 'PRICE_SUGGESTION':
      case 'PRICE_SOURCE':
        return { bg: tint.red.soft, text: tint.red.text };
      case 'CUSTOMER_MARKUP':
        return { bg: tint.yellow.medium, text: tint.yellow.text };
      case 'ROUNDING':
        return { bg: tint.sky.strong, text: tint.sky.text };
      default:
        return { bg: palette.divider, text: palette.text.primary };
    }
  }, [calculusArea, palette]);

  return (
    <Chip
      rounded={rounded}
      sx={{ width: width, height: height }}
      backgroundColor={chipColor.bg}
      textColor={chipColor.text}
      label={
        <Stack alignItems="center" flexDirection="column">
          {customLabel ?? chipLabel}

          <Typography variant="body2">{labelDetails}</Typography>
        </Stack>
      }
    />
  );
};
